// Service pour calculer les statistiques d'attestation et déterminer le winner
import { ref, Ref, computed } from 'vue'

export interface AttestationStats {
  userId: string
  lastName: string
  firstName: string
  attestationCount: number
  isWinner: boolean
}

export interface Winner {
  userId: string | null
  lastName: string
  firstName: string
  attestationCount: number
}

class AttestationStatsService {
  private stats: Ref<AttestationStats[]> = ref([])
  private currentWinner: Ref<Winner> = ref({
    userId: null,
    lastName: 'Aucun',
    firstName: 'Winner',
    attestationCount: 0,
  })
  private pollInterval: number | null = null

  /**
   * Récupère le leader des attestations (celui avec le plus d'attestations)
   * Appelle le backend pour obtenir les statistiques
   */
  async fetchLeaderStats(): Promise<Winner> {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
      const response = await fetch(`${apiBaseUrl}/attestations/stats/leader`, {
        credentials: 'include',
      })

      if (!response.ok) {
        console.warn('Erreur lors de la récupération des stats du leader:', response.status)
        return this.currentWinner.value
      }

      const data = (await response.json()) as Winner
      this.currentWinner.value = {
        userId: data.userId,
        lastName: data.lastName || 'Aucun',
        firstName: data.firstName || 'Winner',
        attestationCount: data.attestationCount || 0,
      }

      return this.currentWinner.value
    } catch (error) {
      console.error('Erreur lors du fetch des stats du leader:', error)
      return this.currentWinner.value
    }
  }

  /**
   * Récupère toutes les statistiques des utilisateurs (admin/super-admin)
   */
  async fetchAllStats(): Promise<AttestationStats[]> {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
      const response = await fetch(`${apiBaseUrl}/attestations/stats/all`, {
        credentials: 'include',
      })

      if (!response.ok) {
        console.warn('Erreur lors de la récupération des stats:', response.status)
        return this.stats.value
      }

      const data = (await response.json()) as AttestationStats[]

      // Déterminer le winner et marquer les utilisateurs
      if (data.length > 0) {
        const maxCount = Math.max(...data.map((s) => s.attestationCount))
        this.stats.value = data.map((stat: AttestationStats) => ({
          ...stat,
          isWinner: stat.attestationCount === maxCount && maxCount > 0,
        }))

        // Mettre à jour le winner actuel
        const winner = this.stats.value.find((s) => s.isWinner)
        if (winner) {
          this.currentWinner.value = {
            userId: winner.userId,
            lastName: winner.lastName,
            firstName: winner.firstName,
            attestationCount: winner.attestationCount,
          }
        }
      }

      return this.stats.value
    } catch (error) {
      console.error('Erreur lors du fetch des stats:', error)
      return this.stats.value
    }
  }

  /**
   * Retourne le winner actuel
   */
  getWinner(): Winner {
    return this.currentWinner.value
  }

  /**
   * Retourne le winner en tant que ref (pour réactivité Vue)
   */
  getWinnerRef(): Ref<Winner> {
    return this.currentWinner
  }

  /**
   * Démarre une mise à jour périodique du winner (polling)
   * @param intervalMs Intervalle en ms pour mettre à jour (défaut 10000 = 10s)
   */
  startPolling(intervalMs: number = 10000): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }

    // Première mise à jour immédiate
    this.fetchLeaderStats()

    // Mise à jour périodique
    this.pollInterval = window.setInterval(() => {
      this.fetchLeaderStats()
    }, intervalMs)
  }

  /**
   * Arrête la mise à jour périodique
   */
  stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  /**
   * Réinitialise le service
   */
  reset(): void {
    this.stopPolling()
    this.stats.value = []
    this.currentWinner.value = {
      userId: null,
      lastName: 'Aucun',
      firstName: 'Winner',
      attestationCount: 0,
    }
  }
}

// Instance singleton
export const attestationStatsService = new AttestationStatsService()
